import {
  PayReportingTableRowViewModel,
  PayReportingTableRowViewModelOld,
} from '@fora-monorepo/employer-my-pages-common/types';
import { HeadCellSortState } from '@foradevelopment/fora-ui';

import { PayrollFormData } from '../../payroll/types';
import type { TableSortId } from '../types';
import {
  sortBoolean,
  sortBooleanAndStringBasic,
  sortInactiveOrHasDeviation,
  sortKeyHasParams,
  sortKeyInactive,
  sortKeyNone,
  sortNumber,
  sortStringBasic,
  sortStringLocale,
} from './comparisonFunctions';

type TableSortState = {
  [key in TableSortId]?: HeadCellSortState;
};

export const sortPayReportingTableDataListIndices = ({
  payReportingTableData,
  sortState,
  indices,
  payrollData,
}: {
  payReportingTableData: PayReportingTableRowViewModel[] | PayReportingTableRowViewModelOld[];
  sortState: TableSortState;
  indices?: number[];
  payrollData?: PayrollFormData;
}): number[] => {
  const indicesToSort = indices ?? payReportingTableData.map((_, ind) => ind);

  if (Object.keys(sortState).every(state => !state)) {
    return indicesToSort;
  }

  indicesToSort.sort((a: number, b: number) => {
    const payReportDraftDataA = payReportingTableData[a];
    const payReportDraftDataB = payReportingTableData[b];

    // Default sorting by employeeId
    if (sortState.employeeId) {
      return sortStringBasic(
        payReportDraftDataA.employee.employeeId,
        payReportDraftDataB.employee.employeeId,
        sortState.employeeId === HeadCellSortState.Asc
      );
    }
    // Sorting by first name
    if (sortState.firstName) {
      return sortStringLocale(
        payReportDraftDataA.employee.firstName,
        payReportDraftDataB.employee.firstName,
        sortState.firstName === HeadCellSortState.Asc
      );
    }
    // Sorting by last name
    if (sortState.lastName) {
      return sortStringLocale(
        payReportDraftDataA.employee.lastName,
        payReportDraftDataB.employee.lastName,
        sortState.lastName === HeadCellSortState.Asc
      );
    }
    // // Sorting by markup (Uppmärkning)
    if (sortState.markup && (payReportDraftDataA.activeMarkup?.fokId || payReportDraftDataB.activeMarkup?.fokId)) {
      return sortStringLocale(
        payReportDraftDataA.activeMarkup?.fokId ?? '',
        payReportDraftDataB.activeMarkup?.fokId ?? '',
        sortState.markup === HeadCellSortState.Asc
      );
    }

    // Sorting by fokParameterChoices / inactive icon column
    if (sortState.inactiveOrFokParameterChoices) {
      return sortInactiveOrHasDeviation(
        payReportDraftDataA.isInactive
          ? sortKeyInactive
          : payReportDraftDataA.hasFokParameterChoices
            ? sortKeyHasParams
            : sortKeyNone,
        payReportDraftDataB.isInactive
          ? sortKeyInactive
          : payReportDraftDataB.hasFokParameterChoices
            ? sortKeyHasParams
            : sortKeyNone,
        sortState.inactiveOrFokParameterChoices === HeadCellSortState.Asc
      );
    }

    // Sorting by has attributablePay (hänförlig lön)
    if (sortState.attributablePay && payrollData) {
      return sortBoolean(
        !!payrollData[payReportDraftDataA.policyNumber]?.attributablePay?.length,
        !!payrollData[payReportDraftDataB.policyNumber]?.attributablePay?.length,
        sortState.attributablePay === HeadCellSortState.Asc
      );
    }

    // Sorting by pay amount
    if (sortState.payAmount && payrollData) {
      return sortNumber(
        payrollData[payReportDraftDataA.policyNumber]?.payAmount ?? 0,
        payrollData[payReportDraftDataB.policyNumber]?.payAmount ?? 0,
        sortState.payAmount === HeadCellSortState.Asc
      );
    }

    // Sorting by has hasError (Validation error)
    if (sortState.hasError && payrollData) {
      return sortBooleanAndStringBasic(
        {
          bool: !!payrollData[payReportDraftDataB.policyNumber]?.hasError,
          stringBasic: payReportDraftDataA.employee.employeeId,
        },
        {
          bool: !!payrollData[payReportDraftDataA.policyNumber]?.hasError,
          stringBasic: payReportDraftDataB.employee.employeeId,
        },
        true
      );
    }

    return 0;
  });
  return indicesToSort;
};
